import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { formatDistanceToNow, format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { processAbuseBlocks, BlockDisplay } from "@/lib/abuseUtils";

export default function Abuse() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [organizationId, setOrganizationId] = useState<string | null>(null);
    const [planKey, setPlanKey] = useState<string | null>(null);

    // 1. Fetch User's Org
    const { data: orgData, isLoading: isOrgLoading } = useQuery({
        queryKey: ["admin-abuse-org"],
        queryFn: async () => {
            const { data: user } = await supabase.auth.getUser();
            if (!user.user) throw new Error("Not authenticated");

            // Get first org for now
            const { data: member } = await supabase
                .from("organization_members")
                .select("organization_id, organizations(plan_key)")
                .eq("user_id", user.user.id)
                .single();

            if (!member || !member.organization_id) throw new Error("No organization found");

            setOrganizationId(member.organization_id);
            setPlanKey(member.organizations?.plan_key || 'free');

            return member;
        }
    });

    // 2. Fetch Abuse Blocks + Participants + Contests
    const { data: blocks, isLoading: isBlocksLoading, refetch } = useQuery({
        queryKey: ["admin-abuse-blocks-grouped", organizationId],
        enabled: !!organizationId,
        queryFn: async () => {
            // Fetch blocks first to determine scope
            const { data: fetchedBlocks, error: blocksError } = await supabase
                .from("abuse_blocks")
                .select("*")
                .eq("organization_id", organizationId)
                .order("created_at", { ascending: false });

            if (blocksError) {
                toast({ title: "Failed to load blocks", description: blocksError.message, variant: "destructive" });
                throw blocksError;
            }

            // Extract unique contest IDs from blocks
            const contestIds = new Set<string>();
            fetchedBlocks?.forEach((b: any) => {
                if (b.contest_id) contestIds.add(b.contest_id);
            });

            // Fetch Contest Titles (STILL NEEDED for mapping logic in processAbuseBlocks,
            // even if not displayed directly in column, the abuseUtils might rely on it or we might re-add later)
            // But user said: "Remove unused contest title mapping logic if it is now redundant."
            // processAbuseBlocks uses contestMap to populate contest_titles in BlockDisplay.
            // If we are not displaying contest_titles, we don't technically need the map.
            // However, processAbuseBlocks expects the Map.
            // We can pass an empty map if we don't care, or keep it to be safe.
            // The User request: "Remove unused contest title mapping logic IF it is now redundant."
            // Since we are removing the column, the mapping IS redundant for display.
            // But let's check abuseUtils.ts to see if it breaks without it.
            // It just updates `row.contest_titles`. If we don't use `row.contest_titles`, we can skip fetching.
            // BUT, for participant resolution, we DO need `contestIds` to fetch `targetedParticipants`.

            // So we keep contestIds extraction.
            // We can skip fetching Titles if we want.
            // Let's create an empty map to pass to the util to avoid changing the utility signature.
            const contestMap = new Map<string, string>();

            // We still need contestIds for fetching participants

            // Fetch Participants for Resolution
            let participants: any[] = [];

            if (contestIds.size > 0) {
                const { data: targetedParticipants, error: tpError } = await supabase
                    .from("participants")
                    .select("id, email, ip_hash, device_hash, contest_id")
                    .in("contest_id", Array.from(contestIds))
                    .order("created_at", { ascending: false })
                    .limit(2000); // Generous limit for targeted fetch

                if (!tpError && targetedParticipants) {
                    participants = targetedParticipants;
                }
            }

            // If we have few participants (e.g. only org blocks exist), fetch generic recent ones
            if (participants.length < 500) {
                // Fetch all contests for org to query recent participants generically
                const { data: allContests } = await supabase.from("contests").select("id").eq("organization_id", organizationId);
                if (allContests && allContests.length > 0) {
                    const allContestIds = allContests.map(c => c.id);
                    const { data: genericParticipants } = await supabase
                        .from("participants")
                        .select("id, email, ip_hash, device_hash, contest_id")
                        .in("contest_id", allContestIds)
                        .order("created_at", { ascending: false })
                        .limit(500);

                    if (genericParticipants) {
                        // Merge distinct participants
                        const existingIds = new Set(participants.map(p => p.id));
                        genericParticipants.forEach(p => {
                            if (!existingIds.has(p.id)) participants.push(p);
                        });
                    }
                }
            }

            return processAbuseBlocks(fetchedBlocks || [], participants || [], contestMap);
        }
    });

    const handleUnblock = async (row: BlockDisplay) => {
        if (!planKey || planKey === 'free') {
            toast({
                title: "Upgrade Required",
                description: "Unblocking requires a paid plan.",
                action: (
                    <ToastAction altText="Upgrade" onClick={() => window.location.assign("/admin/settings?tab=billing")}>
                        Upgrade
                    </ToastAction>
                ),
            });
            return;
        }

        // Unblock ALL blocks associated with this row (person)
        let successCount = 0;
        let failCount = 0;

        for (const blockId of row.block_ids) {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                const { error } = await supabase.functions.invoke('admin-abuse-unblock', {
                    body: { abuse_block_id: blockId },
                    headers: { Authorization: `Bearer ${session?.access_token}` }
                });

                if (error) throw error;
                successCount++;
            } catch (err: any) {
                failCount++;
                console.error(`Failed to unblock ${blockId}`, err);
            }
        }

        if (successCount > 0) {
            toast({ title: `Unblocked ${successCount} rule(s)` });
            // Invalidate all relevant queries to ensure UI updates everywhere
            refetch();
            void queryClient.invalidateQueries({ queryKey: ["admin-participants-list"] });
            void queryClient.invalidateQueries({ queryKey: ["admin-abuse-blocks"] }); // Just in case
        }
        if (failCount > 0) {
            toast({ title: `Failed to unblock ${failCount} rule(s)`, variant: "destructive" });
        }
    };

    if (isOrgLoading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

    return (
        <div className="container mx-auto py-8">
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Abuse Management</h1>
                    <p className="text-muted-foreground">Manage blocked users and spam prevention rules.</p>
                </div>
            </div>

            {isBlocksLoading ? (
                <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : (!blocks || blocks.length === 0) ? (
                <div className="rounded-lg border border-dashed p-8 text-center">
                    <CheckCircle2 className="mx-auto h-12 w-12 text-muted-foreground/50" />
                    <h3 className="mt-4 text-lg font-semibold">No active blocks</h3>
                    <p className="text-sm text-muted-foreground">Your blocklist is empty.</p>
                </div>
            ) : (
                <div className="rounded-md border bg-card">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[200px]">User</TableHead>
                                <TableHead>Blocked Identifiers</TableHead>
                                <TableHead>Scope</TableHead>
                                <TableHead>Reason</TableHead>
                                <TableHead>Expires</TableHead>
                                <TableHead>Date Added</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {blocks.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell className="font-medium">
                                        {row.email ? (
                                            <div className="flex items-center gap-2">
                                                <span className="truncate max-w-[180px]" title={row.email}>{row.email}</span>
                                            </div>
                                        ) : (
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div className="flex items-center gap-2 cursor-help text-muted-foreground">
                                                            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                                            <span className="truncate max-w-[150px] font-mono text-xs">
                                                                {row.identifier_value.substring(0, 12)}...
                                                            </span>
                                                        </div>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Unresolved Identifier</p>
                                                        <p className="text-xs font-mono break-all">{row.identifier_value}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {Array.from(row.identifier_types).map(type => (
                                                <Badge key={type} variant="outline" className="text-xs capitalize whitespace-nowrap">
                                                    {type.replace('_hash', '').replace('participant_id', 'Participant')}
                                                </Badge>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={row.scope === 'org' ? "default" : "secondary"}>
                                            {row.scope === 'org' ? 'Organization' : 'Contest'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <span className="text-sm truncate max-w-[200px] block" title={Array.from(row.reasons).join(", ")}>
                                            {Array.from(row.reasons)[0]}
                                            {row.reasons.size > 1 && ` (+${row.reasons.size - 1})`}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {row.expires_at ? (
                                            row.expires_at === 'MIN_DATE' ? <span className="text-muted-foreground text-xs">Never</span> : // Fallback
                                                new Date(row.expires_at) < new Date() ?
                                                    <span className="text-destructive text-xs font-medium">Expired</span> :
                                                    <span className="text-xs">{format(new Date(row.expires_at), 'MMM d, yyyy')}</span>
                                        ) : (
                                            <span className="text-muted-foreground text-xs">Never</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-xs">
                                        {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleUnblock(row)}
                                            className="text-destructive hover:bg-destructive/10 hover:text-destructive h-8 px-2"
                                            disabled={planKey === 'free'}
                                        >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            Unblock
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}
